import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Lock, LogOut, Send, Truck, ExternalLink, HandHeart, StickyNote } from "lucide-react";
import FlowerButton from "@/components/FlowerButton";
import { StockPanel } from "@/components/StockPanel";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (c) => `${(c / 100).toFixed(2).replace(".", ",").replace(",00", "")} €`;
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const STATUS = {
  paid: { label: "À préparer", cls: "bg-[#FFF2B2]" },
  shipped: { label: "Expédiée", cls: "bg-[#C8D0B7]" },
  delivered: { label: "Remise en mains propres", cls: "bg-[#FADADD]" },
};

const TrackingForm = ({ order, carriers, secret, onDone }) => {
  const [carrier, setCarrier] = useState(order.carrier || "chronopost");
  const [number, setNumber] = useState(order.tracking_number || "");
  const [url, setUrl] = useState(order.tracking_url || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/orders/${order.session_id}/tracking`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-Admin-Secret": secret },
        body: JSON.stringify({ carrier, tracking_number: number, tracking_url: carrier === "other" ? url : null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur");
      toast.success("Colis marqué expédié — e-mail de suivi envoyé ✿");
      onDone(data.order);
    } catch (err) {
      toast.error(err.message || "Impossible d'enregistrer le suivi");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3" data-testid={`tracking-form-${order.session_id}`}>
      <label className="text-xs text-[#4A3B32]/70">Transporteur
        <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="tracking-carrier"
          className="block mt-1 rounded-full border-2 border-[#D1D5CB] bg-[#FFFDF9] px-3 py-2 text-sm text-[#4A3B32]">
          {Object.entries(carriers).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      <label className="text-xs text-[#4A3B32]/70 flex-1 min-w-[160px]">N° de suivi
        <input value={number} onChange={(e) => setNumber(e.target.value)} required data-testid="tracking-number"
          className="block mt-1 w-full rounded-full border-2 border-[#D1D5CB] bg-[#FFFDF9] px-3 py-2 text-sm text-[#4A3B32]" placeholder="XY123456789FR" />
      </label>
      {carrier === "other" && (
        <label className="text-xs text-[#4A3B32]/70 flex-1 min-w-[200px]">Lien de suivi (https)
          <input value={url} onChange={(e) => setUrl(e.target.value)} required data-testid="tracking-url"
            className="block mt-1 w-full rounded-full border-2 border-[#D1D5CB] bg-[#FFFDF9] px-3 py-2 text-sm text-[#4A3B32]" placeholder="https://…" />
        </label>
      )}
      <button type="submit" disabled={busy} data-testid="tracking-submit"
        className="inline-flex items-center gap-2 rounded-full bg-[#C8D0B7] text-[#4A3B32] px-4 py-2 font-cursive text-xl disabled:opacity-60">
        <Truck className="w-4 h-4" /> {order.tracking_number ? "Mettre à jour" : "Expédier"}
      </button>
    </form>
  );
};

const NoteBox = ({ order, secret, onDone }) => {
  const [note, setNote] = useState(order.note || "");
  const [busy, setBusy] = useState(false);
  const dirty = note !== (order.note || "");
  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/orders/${order.session_id}/note`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-Admin-Secret": secret }, body: JSON.stringify({ note }),
      });
      if (!res.ok) throw new Error();
      toast.success("Note enregistrée ✿");
      onDone((await res.json()).order);
    } catch { toast.error("Impossible d'enregistrer la note"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-start gap-2" data-testid={`note-box-${order.session_id}`}>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={1} maxLength={500} placeholder="Note privée (ex. « livrée au marché samedi »)…" data-testid="order-note-input"
        className="flex-1 min-w-[220px] rounded-2xl border-2 border-dashed border-[#D1D5CB] bg-[#FFFDF9] px-3 py-2 text-sm text-[#4A3B32] placeholder-[#4A3B32]/40 focus:outline-none focus:border-[#78353A] resize-y" />
      {dirty && (
        <button type="button" onClick={save} disabled={busy} data-testid="order-note-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#FFF2B2] text-[#4A3B32] px-4 py-2 font-cursive text-xl disabled:opacity-60">
          <StickyNote className="w-4 h-4" /> Enregistrer
        </button>
      )}
    </div>
  );
};

const OrderCard = ({ order, carriers, secret, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const resend = async () => {
    const res = await fetch(`${API}/admin/orders/${order.session_id}/resend-confirmation`, { method: "POST", headers: { "X-Admin-Secret": secret } });
    res.ok ? toast.success("Confirmation renvoyée ✿") : toast.error("Envoi impossible");
  };
  const handDeliver = async () => {
    if (!window.confirm(`Marquer la commande ${order.order_number} comme remise en mains propres ? Aucun e-mail ne sera envoyé.`)) return;
    const res = await fetch(`${API}/admin/orders/${order.session_id}/delivered`, { method: "POST", headers: { "X-Admin-Secret": secret } });
    if (!res.ok) return toast.error("Mise à jour impossible");
    const data = await res.json();
    toast.success("Commande marquée remise en mains propres ✿");
    onUpdate(data.order);
  };
  const a = order.shipping_address || {};
  const st = STATUS[order.status] || STATUS.paid;
  return (
    <div className="rounded-3xl border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] p-5" data-testid={`order-${order.session_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-cursive text-3xl text-[#78353A] leading-none">{order.order_number}
            <span className={`ml-3 align-middle text-xs px-3 py-1 rounded-full ${st.cls} text-[#4A3B32]`} data-testid="order-status">
              {st.label}
            </span>
            {!order.live && <span className="ml-2 align-middle text-xs px-2 py-1 rounded-full bg-[#EAECE1] text-[#4A3B32]/70">test</span>}
          </p>
          <p className="text-sm text-[#4A3B32]/80 mt-1">{date(order.created_at)} · {order.customer_name} · {order.customer_email}</p>
        </div>
        <p className="font-cursive text-3xl text-[#4A3B32]">{eur(order.amount_total)}</p>
      </div>
      <ul className="mt-3 text-sm text-[#4A3B32]/90">
        {order.items.map((i, k) => <li key={k}>{i.quantity} × {i.name} {i.amount === 0 && <em className="text-[#78353A]/70">(offert)</em>}</li>)}
      </ul>
      <p className="mt-2 text-sm text-[#4A3B32]/80">📦 {order.shipping_name || order.customer_name}, {[a.line1, a.line2, a.postal_code, a.city, a.country].filter(Boolean).join(", ")}</p>
      {order.tracking_url && (
        <p className="mt-2 text-sm"><a href={order.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#78353A] underline decoration-dotted">{carriers[order.carrier]} · {order.tracking_number} <ExternalLink className="w-3 h-3" /></a></p>
      )}
      {order.status === "delivered" && (
        <p className="mt-2 text-sm text-[#78353A]">Remise en mains propres le {date(order.delivered_at)}</p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid="toggle-tracking" className="rounded-full border-2 border-dashed border-[#C8D0B7] px-4 py-2 font-cursive text-xl text-[#78353A]">
          {order.status === "shipped" ? "Modifier le suivi" : "Saisir le n° de suivi"}
        </button>
        {order.status !== "delivered" && (
          <button type="button" onClick={handDeliver} data-testid="hand-deliver" className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-[#FADADD] px-4 py-2 font-cursive text-xl text-[#78353A]">
            <HandHeart className="w-4 h-4" /> Remise en mains propres
          </button>
        )}
        <button type="button" onClick={resend} data-testid="resend-confirmation" className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-cursive text-xl text-[#78353A]/80 hover:bg-[#FADADD]/50">
          <Send className="w-4 h-4" /> Renvoyer la confirmation
        </button>
      </div>
      {open && <div className="mt-4"><TrackingForm order={order} carriers={carriers} secret={secret} onDone={(o) => { onUpdate(o); setOpen(false); }} /></div>}
      <NoteBox key={order.note || ""} order={order} secret={secret} onDone={onUpdate} />
    </div>
  );
};

export default function AdminOrders() {
  const [secret, setSecret] = useState(() => sessionStorage.getItem("mm_admin_secret") || "");
  const [input, setInput] = useState("");
  const [orders, setOrders] = useState(null);
  const [carriers, setCarriers] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async (key, sync = true) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/orders?sync=${sync}`, { headers: { "X-Admin-Secret": key } });
      if (res.status === 401) { sessionStorage.removeItem("mm_admin_secret"); setSecret(""); toast.error("Mot de passe incorrect"); return; }
      const data = await res.json();
      setOrders(data.orders); setCarriers(data.carriers);
      sessionStorage.setItem("mm_admin_secret", key); setSecret(key);
    } catch { toast.error("Impossible de charger les commandes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (secret) load(secret); }, []); // eslint-disable-line

  if (!secret || orders === null) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="admin-orders-login">
        <Lock className="w-8 h-8 mx-auto text-[#78353A]" />
        <h1 className="font-cursive text-5xl text-[#78353A] mt-3">Commandes</h1>
        <form onSubmit={(e) => { e.preventDefault(); load(input); }} className="mt-6 flex flex-col items-center gap-4">
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Mot de passe" data-testid="admin-orders-secret-input"
            className="w-full rounded-full border-2 border-[#D1D5CB] bg-[#FFFDF9] px-5 py-3 text-[#4A3B32]" />
          <FlowerButton type="submit" data-testid="admin-orders-login-btn">{loading ? "…" : "Entrer"}</FlowerButton>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-orders">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-cursive text-2xl text-[#78353A]/70">Espace privé</p>
          <h1 className="font-cursive text-6xl text-[#78353A] leading-none">Commandes</h1>
          <p className="mt-2 text-sm text-[#4A3B32]/70"><span data-testid="orders-count">{orders.length}</span> commande(s) · <Link to="/admin/newsletter" className="underline decoration-dotted">Les potes qui papotent →</Link> · <Link to="/admin/avis" className="underline decoration-dotted">Les mots doux →</Link></p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load(secret)} data-testid="orders-refresh" className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-[#D1D5CB] px-4 py-2 font-cursive text-xl text-[#78353A]"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Synchroniser Stripe</button>
          <button onClick={() => { sessionStorage.removeItem("mm_admin_secret"); setSecret(""); setOrders(null); }} data-testid="orders-logout" className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-cursive text-xl text-[#78353A]/70"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
      <StockPanel secret={secret} />
      <div className="space-y-5">
        {orders.length === 0 && <p className="text-center font-cursive text-3xl text-[#78353A]/60 py-16">Aucune commande pour le moment ✿</p>}
        {orders.map((o) => <OrderCard key={o.session_id} order={o} carriers={carriers} secret={secret} onUpdate={(u) => setOrders((list) => list.map((x) => (x.session_id === u.session_id ? u : x)))} />)}
      </div>
    </div>
  );
}
